import { useHistory, Link } from 'react-router-dom';
import MyWorkspaceTable from '../MyWorkspaceTable';
import intl from 'react-intl-universal';
import { Tooltip } from '@getsynapse/design-system';
import { PATHS, DATE } from 'utils/constants';
import get from 'lodash/get';
import { Task } from 'utils/customTypes';
import moment from '../../../../../utils/moment';
import {
  getStatusColumn,
  getStatusPill,
} from 'Pages/ProjectsListPage/helpers/tableColumnsValues';

type TasksTableProps = {
  data: Task[];
  handleSort: (orderByParam: string[], order: 'desc' | 'asc') => void;
  cardTable: boolean;
};

const TasksTable = ({ data, handleSort, cardTable }: TasksTableProps) => {
  const history = useHistory();

  return (
    <MyWorkspaceTable
      cardTable={cardTable}
      dataCy='due-tasks-table'
      headCells={[
        {
          content: intl.get('MY_WORKSPACE.DUE_TASKS.TABLE.HEAD_CELLS.TITLE'),
          columnName: 'name',
        },
        {
          content: intl.get('MY_WORKSPACE.DUE_TASKS.TABLE.HEAD_CELLS.PROJECT'),
          columnName: 'projectLearnOp.title',
        },
        {
          content: intl.get('MY_WORKSPACE.DUE_TASKS.TABLE.HEAD_CELLS.STATUS'),
          columnName: 'status',
          className: `${cardTable ? 'w-20' : 'w-28'}`,
        },
        {
          content: cardTable
            ? intl.get('MY_WORKSPACE.DUE_TASKS.TABLE.HEAD_CELLS.DUE')
            : intl.get('MY_WORKSPACE.DUE_TASKS.TABLE.HEAD_CELLS.DUE_DATE'),
          columnName: 'due_date',
          className: 'w-36',
        },
      ]}
      rows={data.map((task) => {
        const dueDateWithoutTime = moment(new Date(task.due_date!))
          .locale('en')
          .format('L');
        const currentDate = moment(new Date());
        const currentTime = currentDate.format('HH:mm:ssZ');
        const momentDueDate = moment(`${dueDateWithoutTime} ${currentTime}`);
        let dueDateFromNow = momentDueDate.fromNow();
        if (
          dueDateFromNow.includes('hour') ||
          dueDateFromNow.includes('minute') ||
          dueDateFromNow.includes('second')
        ) {
          dueDateFromNow = intl.get('MY_WORKSPACE.DUE_TASKS.TABLE.TODAY');
        }
        const dueDate = cardTable
          ? dueDateFromNow
          : momentDueDate.format(DATE.SHORT_FORMAT);
        const dDiff = momentDueDate.diff(currentDate, 'days');
        const daysBetween = currentDate.date() - momentDueDate.date();
        let dueDateStyle = '';
        if (dDiff < 0) {
          dueDateStyle = 'text-error-darker';
          if (!cardTable) {
            dueDateStyle = dueDateStyle + ' bg-error-lightest';
          }
        } else if (dDiff === 0 && daysBetween === 0) {
          dueDateStyle = 'text-warning-darker';
        }
        const dueDateComponent = (
          <span className={dueDateStyle}>
            {dueDate.charAt(0).toUpperCase() + dueDate.slice(1)}
          </span>
        );

        return {
          id: task.id,
          className: 'table w-full table-fixed cursor-pointer',
          onClick: () =>
            history.push(
              `${PATHS.PROJECT_PAGE}/${get(task, 'projectLearnOp.id')}${
                PATHS.TASKS
              }/${task.id}`,
              { from: PATHS.ROOT }
            ),
          cells: [
            {
              content: (
                <Link
                  to={`${PATHS.PROJECT_PAGE}/${get(task, 'projectLearnOp.id')}${
                    PATHS.TASKS
                  }/${task.id}`}
                  title={task.name}
                >
                  {task.name}
                </Link>
              ),
              className: 'truncate',
            },
            {
              content: (
                <span title={get(task, 'projectLearnOp.title')}>
                  {get(task, 'projectLearnOp.title')}
                </span>
              ),
              className: 'truncate',
            },
            {
              content: cardTable
                ? getStatusPill(task.status)
                : getStatusColumn(task.status),
              className: `${cardTable ? 'w-20' : 'w-28'}`,
            },
            {
              content: cardTable ? (
                dueDateComponent
              ) : (
                <Tooltip
                  trigger={dueDateComponent}
                  showPopper
                  openMode='hover1'
                  timeout={0}
                  contentProps={{ className: 'z-200000 py-1 px-3' }}
                >
                  {dueDateFromNow.charAt(0).toUpperCase() +
                    dueDateFromNow.slice(1)}
                </Tooltip>
              ),
              className: 'w-36',
            },
          ],
        };
      })}
      handleSort={handleSort}
    />
  );
};

export default TasksTable;
